import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { LanguageProvider } from '@/contexts/LanguageContext';

// Loading component for suspense fallback
const Loading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    <p className="ml-3">Loading...</p>
  </div>
);

// Lazy-loaded pages for less critical paths
const Index = lazy(() => import('./pages/index'));
const NotFound = lazy(() => import('./pages/NotFound'));
const EsimForHotel = lazy(() => import('./pages/EsimForHotel'));
const EsimForHotelContract = lazy(() => import('./pages/EsimForHotelContract'));
const AgentPrivacyPolicy = lazy(() => import('./pages/AgentPrivacyPolicy'));
const Admin = lazy(() => import('./pages/Admin'));
const Products = lazy(() => import('./pages/admin/Products'));
const EsimInventoryPage = lazy(() => import('./pages/admin/EsimInventoryPage'));
const OrdersPage = lazy(() => import('./pages/admin/OrdersPage'));
const AdminPasswordReset = lazy(() => import('./pages/AdminPasswordReset'));
const AdminDebug = lazy(() => import('./pages/AdminDebug'));
const MyApp = lazy(() => import('./pages/MyPage'));
const Auth = lazy(() => import('./pages/Auth'));
const Test = lazy(() => import('./pages/Test'));

// Google Analytics page view tracker
function GAPageTracker() {
  const location = useLocation();
  const initializedRef = useRef(false);
  const [gaReady, setGaReady] = useState(false);

  // 初回ロード時にGeo情報を取得し、GAのuser_propertiesに設定
  useEffect(() => {
    let timeoutId: number | undefined;
    const setup = async () => {
      try {
        // フェールセーフ: 2秒で諦めて続行
        timeoutId = window.setTimeout(() => setGaReady(true), 2000);
        const res = await fetch('/api/geo', { credentials: 'same-origin' });
        if (res.ok) {
          const data = await res.json();
          if (typeof window !== 'undefined' && (window as any).gtag) {
            (window as any).gtag('set', 'user_properties', {
              user_country: data.country || undefined,
              user_region: data.region || undefined,
              user_city: data.city || undefined,
            });
          }
        }
      } catch (_) {
        // noop
      } finally {
        if (timeoutId) window.clearTimeout(timeoutId);
        setGaReady(true);
      }
    };
    setup();
  }, []);

  useEffect(() => {
    // Send pageview to Google Analytics when route changes
    if (gaReady && typeof window !== 'undefined' && (window as any).gtag) {
      const params = new URLSearchParams(window.location.search);
      const isDebug = params.has('ga_debug');
      const options: Record<string, any> = {
        page_path: location.pathname + location.search,
      };
      if (isDebug) options.debug_mode = true;
      (window as any).gtag('config', 'G-QZ09CYDV4X', options);
    }
  }, [location, gaReady]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <GAPageTracker />
        <Routes>
          <Route path="/" element={<Navigate to="/esimforhotel" replace />} />

          {/* eSIM For Hotel LP */}
          <Route path="/esimforhotel" element={
            <Suspense fallback={<Loading />}>
              <EsimForHotel />
            </Suspense>
          } />

          {/* eSIM For Hotel Contract */}
          <Route path="/esimforhotel/contract" element={
            <Suspense fallback={<Loading />}>
              <EsimForHotelContract />
            </Suspense>
          } />

          {/* Agent Privacy Policy */}
          <Route path="/esimforhotel/privacypolicy" element={
            <Suspense fallback={<Loading />}>
              <AgentPrivacyPolicy />
            </Suspense>
          } />

          {/* 管理画面関連 */}
          <Route path="/admin" element={
            <Suspense fallback={<Loading />}>
              <Admin />
            </Suspense>
          } />
          <Route path="/admin/products" element={
            <Suspense fallback={<Loading />}>
              <Products />
            </Suspense>
          } />
          <Route path="/admin/esiminventory" element={
            <Suspense fallback={<Loading />}>
              <EsimInventoryPage />
            </Suspense>
          } />
          <Route path="/admin/orders" element={
            <Suspense fallback={<Loading />}>
              <OrdersPage />
            </Suspense>
          } />
          <Route path="/admin/reset-password" element={
            <Suspense fallback={<Loading />}>
              <AdminPasswordReset />
            </Suspense>
          } />
          <Route path="/admin/debug" element={
            <Suspense fallback={<Loading />}>
              <AdminDebug />
            </Suspense>
          } />
          
          {/* テストページ */}
          <Route path="/test" element={
            <Suspense fallback={<Loading />}>
              <Test />
            </Suspense>
          } />
          
          {/* その他共通ページ */}
          <Route path="/404" element={
            <Suspense fallback={<Loading />}>
              <NotFound />
            </Suspense>
          } />
          <Route path="*" element={<Navigate to="/404" replace />} />
          <Route path="/mypage" element={
            <Suspense fallback={<Loading />}>
              <MyApp />
            </Suspense>
          } />
          <Route path="/auth" element={
            <Suspense fallback={<Loading />}>
              <Auth />
            </Suspense>
          } />
        </Routes>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
